import React, { useCallback, useEffect } from "react";
import Draggable from "react-draggable";
import Carat from "../Toolbar/Carat";
import { DraggableKeys } from "../../main";
import { useStore } from "../../stores/store";

interface BaseDraggableProps {
  size?: "sm" | "md" | "lg" | "xl" | "xxl" | "auto";
  initialPosition: "top-right" | "top-left" | "bottom-right" | "bottom-left" | "top-middle" | "bottom-middle";
  className?: string;
  headerText: string;
  children: React.ReactNode;
  visible: boolean;
  toolbar?: React.FC;
  zIndex?: number;
  onClose: () => void;
  onBack?: () => void;
  id: DraggableKeys;
}

const headerHeight = 145;
export const BaseDraggable = (props: BaseDraggableProps) => {
  const { config, isMobile } = useStore();

  const draggableRef = React.useRef<HTMLDivElement>(null);
  const nodeRef = React.useRef(null)
  const [windowSize, setWindowSize] = React.useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });
  const [isLoaded, setIsLoaded] = React.useState(false);
  const [isMinimized, setIsMinimized] = React.useState(false);
  const [bounds, setBounds] = React.useState({
    left: 0,
    top: 0,
    right: 0,
    bottom: 0,
  });
  const [position, setPosition] = React.useState({ x: 0, y: 0 });
  const [userHasDragged, setUserHasDragged] = React.useState(false);

  const updateBounds = useCallback(() => {
    if (!draggableRef || !draggableRef.current || !props.visible) return;
    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;
    const elementHeight = draggableRef.current.clientHeight + 3;
    const elementWidth = draggableRef.current.clientWidth + 3;
    setBounds({
      left: 0,
      top: 0 + headerHeight - 10,
      right: screenWidth - elementWidth,
      bottom: screenHeight - elementHeight,
    });
    // check if the draggable is off the screen
    if (position.x > screenWidth - elementWidth) {
      setPosition({ x: screenWidth - elementWidth, y: position.y });
    }
    if (position.y > screenHeight - elementHeight) {
      setPosition({ x: position.x, y: screenHeight - elementHeight });
    }
  }, [draggableRef, props.visible, isMinimized]);

  useEffect(() => {
    if (!draggableRef.current) return;

    if (props.visible) {
      let elems = document.getElementsByClassName(
        "custom-draggable"
      ) as HTMLCollectionOf<HTMLElement>;

      for (let i = 0; i < elems.length; i++) {
        if (elems[i].style.zIndex === "2") {
          elems[i].style.zIndex = "1";
        }
      }
      if (props.zIndex && props.zIndex > 1) {
        draggableRef.current.style.zIndex = props.zIndex.toString();
      } else {
        draggableRef.current.style.zIndex = "2";
      }
    }
  }, [props.visible, draggableRef]);

  useEffect(() => {
    if (!draggableRef || !draggableRef.current || !props.visible) return;

    updateBounds();
    window.addEventListener("resize", updateBounds);
    return () => {
      window.removeEventListener("resize", updateBounds);
    };
  }, [props.visible, isMinimized]);

  useEffect(() => {
    if (!draggableRef || !draggableRef.current || !props.visible) return;
    updateBounds();
    draggableRef.current.addEventListener("resize", updateBounds);
    return () => {
      draggableRef?.current?.removeEventListener("resize", updateBounds);
    };
  }, [props.visible, isMinimized]);

  useEffect(() => {
    const { width, height } = windowSize;
    const newWidth = window.innerWidth;
    const newHeight = window.innerHeight;

    const widthRatio = newWidth / width;
    const heightRatio = newHeight / height;

    const newPosition = {
      x: position.x * widthRatio,
      y: position.y * heightRatio,
    };
    setPosition(newPosition);
    setWindowSize({ width: newWidth, height: newHeight });
  }, [window.innerWidth, window.innerHeight]);

  const updatePositionBasedOnSize = () => {
    if (!draggableRef.current) {
      // || isLoaded
      return;
    }
    // if (userHasDragged) return;

    let { clientHeight, clientWidth } = draggableRef.current;
    if (clientHeight === 0 || clientWidth === 0) return;
    const padding = 15;

    let x = 0 + padding;
    let y = 0 + headerHeight;

    let _initialPosition = props.initialPosition;
    if (
      config?.draggablePlacementOverrides &&
      config.draggablePlacementOverrides[props.id]
    ) {
      _initialPosition = config.draggablePlacementOverrides[props.id];
    }

    if (_initialPosition === "top-right") {
      x = window.innerWidth - clientWidth - padding;
    } else if (_initialPosition === "bottom-right") {
      x = window.innerWidth - clientWidth - padding;
      y = window.innerHeight - clientHeight;
    } else if (_initialPosition === "bottom-left") {
      y = window.innerHeight - clientHeight - padding;
    } else if (_initialPosition === "top-left") {
      x += 50;
    } else if (_initialPosition === "top-middle") {
      x = window.innerWidth / 2 - clientWidth / 2;
    }
    else if (_initialPosition === "bottom-middle") {
      x = window.innerWidth / 2 - clientWidth / 2;
      y = window.innerHeight - clientHeight - padding;
    }
    setPosition({ x, y });
    setIsLoaded(true);
  };

  useEffect(() => {
    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        if (entry.contentBoxSize) {
          updateBounds();
          updatePositionBasedOnSize();
        }
      }
    });
    if (draggableRef.current) {
      resizeObserver.observe(draggableRef.current);
    }
    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <>
      {isMobile && (
        <>
          {
            props.visible && <div className="absolute top-0 left-0 w-full h-full bg-white z-10">
              <header>
                <div className="flex items-center justify-between p-2 bg-blue-950 text-white font-bold">
                  {props.onBack && props.onBack !== undefined &&
                    <button
                      onTouchStart={props.onBack}
                      onClick={props.onBack}
                      className="hover:opacity-75 text-lg cursor-pointer btn btn-athena btn-xs text-sm text-white"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width={14} height={14} viewBox="0 0 15 15"><path fill="white" fillRule="evenodd" d="M8.818 4.182a.45.45 0 0 1 0 .636L6.136 7.5l2.682 2.682a.45.45 0 1 1-.636.636l-3-3a.45.45 0 0 1 0-.636l3-3a.45.45 0 0 1 .636 0" clipRule="evenodd"></path></svg><span className="">Back</span>
                    </button>
                  }

                  <span>{props.headerText}</span>
                  <button
                    onTouchStart={props.onClose}
                    onClick={props.onClose}
                    className="hover:opacity-75 text-lg cursor-pointer btn btn-athena btn-xs text-sm text-white"
                  >
                    Done
                  </button>
                </div>
              </header>
              <div className="p-2">
                {props.children}
              </div>
            </div>
          }
        </>
      )}
      <Draggable
        position={position}
        onDrag={(e, data) => {
          setUserHasDragged(true);
          setPosition({ x: data.x, y: data.y });
        }}
        bounds={bounds}
        handle=".header"
        nodeRef={draggableRef}
      >
        <div
          ref={draggableRef}
          style={{
            width:
              props.size === "sm"
                ? "200px"
                : props.size === "md"
                  ? "400px"
                  : props.size === "lg"
                    ? "600px"
                    : props.size === "xl"
                      ? "800px"
                      : props.size === "xxl"
                        ? "1200px"
                        : "auto",
            visibility: isLoaded ? "visible" : "hidden",
            zIndex: props.zIndex ? props.zIndex : 1,
            maxWidth: "90vw",
            overflow: "auto",
          }}
          className={`custom-draggable absolute rounded-md shadow-md w-full ${props.visible && !isMobile ? "" : "hidden"
            } ${!props.className || props.className === ""
              ? "bg-white"
              : props.className
            } `}
        >
          <div className="header overflow-hidden rounded-t-md px-2 font-bold text-sm text-white bg-blue-950 flex items-center justify-between">
            <span>{props.headerText}</span>
            <div className="flex items-center cursor-pointer">
              <button
                onTouchStart={() => setIsMinimized(!isMinimized)}
                onClick={() => setIsMinimized(!isMinimized)}
                className="mr-1 hover:opacity-75 cursor-pointer"
              >
                {isMinimized ? (
                  <Carat direction="down" color="white" size={4} />
                ) : (
                  <Carat direction="up" color="white" size={4} />
                )}
              </button>
              <button
                onTouchStart={props.onClose}
                onClick={props.onClose}
                className="hover:opacity-75 text-lg cursor-pointer"
              >
                x
              </button>
            </div>
          </div>
          {props.toolbar && <props.toolbar />}
          <div
            className={`p-2 text-sm transition-all ${isMinimized ? "hidden" : ""
              }`}
          >
            {props.children}
          </div>
          <hr />
        </div>
      </Draggable>
    </>
  );
};
